import { Radio } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import { GiftCardProvider } from 'api/giftCardProviders'
import Badges from 'components/Badges'
import { ColorPicker } from 'components/Form/ColorPicker'
import {
  StandaloneImagePicker,
  StandaloneImagePickerValue,
} from 'components/ImagePicker/StandaloneImagePicker'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { ValidationTypes } from 'models/Validation'
import { useDispatch, useSelector } from 'react-redux'

import {
  UrlPickerGeneric,
  UrlType,
  UrlValue,
} from 'scenes/Cms/components/UrlPickerGeneric'
import {
  cardImageUploaded,
  GiftCardState,
  linkImageUploaded,
  removeCardImage,
  removeLinkImage,
  selectGiftCardProvider,
  setCardBackgroundColor,
  setCardImageType,
  setExternalLinkEnabled,
  setExternalLinkUrl,
  updateCardImage,
  updateLinkImage,
} from 'slices/giftCards'

const t = buildTranslate('gift_cards')

type Props = {
  provider: GiftCardProvider
}

const ProviderConfiguration = ({ provider }: Props) => {
  const isCustomImageEnabled = useFlag('gift-cards-custom-image-mx', false)
  const isExternalLinkFeatureActive = useFlag(
    'gift-cards-external-link-mx',
    false
  )
  const dispatch = useDispatch()
  const providerConfig = useSelector((state: GiftCardState) =>
    selectGiftCardProvider(state, provider.id)
  )

  if (!providerConfig) return null

  const {
    cardImageType,
    externalLinkEnabled,
    cardBackgroundColor,
    externalLinkUrl,
    cardImage,
    linkImage,
  } = providerConfig

  function updateCardImageType(
    cardImageType: GiftCardProvider['card_image_type']
  ) {
    dispatch(
      setCardImageType({
        providerId: provider.id,
        cardImageType,
      })
    )
  }

  function updateExternalLinkEnabled(externalLinkEnabled: boolean) {
    dispatch(
      setExternalLinkEnabled({
        providerId: provider.id,
        externalLinkEnabled,
      })
    )
  }

  function updateBackgroundColor(cardBackgroundColor: string) {
    dispatch(
      setCardBackgroundColor({
        providerId: provider.id,
        cardBackgroundColor,
      })
    )
  }

  function updateExternalLinkUrl(externalLinkUrl: UrlValue) {
    dispatch(
      setExternalLinkUrl({
        providerId: provider.id,
        externalLinkUrl,
      })
    )
  }

  const handleCardImageChange = (fileUploadId: number) => {
    dispatch(
      cardImageUploaded({
        providerId: provider.id,
        fileUploadId,
      })
    )
  }

  const handleCardImageValueChange = (updates: StandaloneImagePickerValue) => {
    dispatch(
      updateCardImage({
        providerId: provider.id,
        updates,
      })
    )
  }

  const handleLinkImageChange = (fileUploadId: number) => {
    dispatch(
      linkImageUploaded({
        providerId: provider.id,
        fileUploadId,
      })
    )
  }

  const handleLinkImageValueChange = (updates: StandaloneImagePickerValue) => {
    dispatch(
      updateLinkImage({
        providerId: provider.id,
        updates,
      })
    )
  }

  return (
    <>
      <Text.Header3 className="mb-xl mt-0">
        {t('configuration.title')}
      </Text.Header3>

      <div className="white-bkg p-m mb-m rounded">
        {/* Card Image Section */}
        <Text.Header4 className="mb-xs">{t('card_image.title')}</Text.Header4>
        <Text.BodyText4 color="grey70">
          {t('card_image.description')}
        </Text.BodyText4>

        {/* Default Card Image Option */}
        <div className="mb-m mt-m">
          <Radio
            className="font-size-14"
            value="default"
            checked={cardImageType === 'default'}
            onChange={() => updateCardImageType('default')}
          >
            <span className="grey-70">{t('card_image.default_option')}</span>
          </Radio>
          <div className="ml-l">
            <Text.BodyText4 color="grey70" className="mt-xs">
              {t('card_image.default_subtext')}
            </Text.BodyText4>
          </div>
        </div>

        {/* Custom Card Image Option */}
        <div className="mb-m">
          <Radio
            className="font-size-14"
            value="custom"
            checked={cardImageType === 'custom'}
            onChange={() => updateCardImageType('custom')}
            disabled={!isCustomImageEnabled}
          >
            <span className="grey-70">{t('card_image.custom_option')}</span>
          </Radio>
          <div className="ml-l">
            <Text.BodyText4 color="grey70" className="mt-xs">
              {t('card_image.custom_subtext')}
            </Text.BodyText4>
          </div>
        </div>

        {/* Color Picker */}
        {cardImageType === 'default' && (
          <div className="mt-l">
            <ColorPicker
              label={t('card_color.label')}
              value={cardBackgroundColor}
              setValue={updateBackgroundColor}
              errorMessage=""
              isPristine
              startEnhancer
            />
          </div>
        )}

        {/* Card Image Upload */}
        {cardImageType === 'custom' && (
          <div className="mt-l">
            <Badges
              className="mb-m"
              values={['app_web', 'resolution', 'jpg', 'png', 'gif'].map(v =>
                t(`card_image.badges.${v}`)
              )}
            />
            <p className="mb-m small-caps-2 bold">{t('card_image.title')}</p>
            <StandaloneImagePicker
              removable
              value={cardImage}
              onChange={handleCardImageValueChange}
              onFinalized={handleCardImageChange}
              onRemoveComplete={() =>
                dispatch(removeCardImage({ providerId: provider.id }))
              }
              minWidth={1372}
              minHeight={860}
              allowedExtensions={['jpg', 'jpeg', 'png', 'gif']}
              record_type={ValidationTypes.GiftCardBackground}
            />
          </div>
        )}
      </div>

      {isExternalLinkFeatureActive && (
        <div className="white-bkg p-m mb-m rounded">
          <Text.Header4 className="mb-xs">
            {t('external_link.title')}
          </Text.Header4>
          <Text.BodyText4 color="grey70">
            {t('external_link.description')}
          </Text.BodyText4>

          <div className="mb-m mt-m">
            <Radio
              className="font-size-14 mb-xs"
              value="yes"
              checked={externalLinkEnabled}
              onChange={() => updateExternalLinkEnabled(true)}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <span className="grey-70">{t('external_link.yes')}</span>
                <Badges values={[t('external_link.recommended')]} />
              </span>
            </Radio>
            <Radio
              className="font-size-14"
              value="no"
              checked={!externalLinkEnabled}
              onChange={() => updateExternalLinkEnabled(false)}
            >
              <span className="grey-70">{t('external_link.no')}</span>
            </Radio>
          </div>

          {/* Link Image and URL Section */}
          {externalLinkEnabled && (
            <>
              <div className="mt-l">
                <label className="mb-xs font-size-14 bold">
                  {t('external_link.link_image.title')}
                </label>
                <Badges
                  className="mb-m"
                  values={[
                    t('external_link.link_image.badges.app_only'),
                    t('external_link.link_image.badges.width'),
                    t('external_link.link_image.badges.height'),
                    t('external_link.link_image.badges.jpg'),
                    t('external_link.link_image.badges.png'),
                  ]}
                />
                <StandaloneImagePicker
                  removable
                  value={linkImage}
                  onChange={handleLinkImageValueChange}
                  onFinalized={handleLinkImageChange}
                  onRemoveComplete={() =>
                    dispatch(removeLinkImage({ providerId: provider.id }))
                  }
                  minWidth={1800}
                  allowedExtensions={['jpg', 'jpeg', 'png']}
                  record_type={ValidationTypes.GiftCardExternalLink}
                />
              </div>

              <div className="mt-l">
                <label
                  className="mb-xs font-size-14 bold"
                  htmlFor="external-link"
                >
                  {t('external_link.link_url.title')}
                </label>
                <UrlPickerGeneric
                  id="external-link"
                  type="image"
                  url={externalLinkUrl}
                  onUrlChange={updateExternalLinkUrl}
                  fixedUrlType={UrlType.External}
                />
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}

export default ProviderConfiguration
